export function extractUrl(input: string): string | null {
  const regex = /^Url\('(.*)'\)$/;
  const match = input.match(regex);
  return match ? match[1] : null;
}

export function isUrlString(input: string): boolean {
  const regex = /^Url\('https?:\/\/[^\s']+'\)$/;
  return regex.test(input);
}

// Each domain label is `alnum (mid-chars alnum)?` — an optional group, not a repeated one.
// A repeated group here makes the number of ways to split a label exponential in its
// length, so any input the pattern ultimately rejects takes exponential time to reject.
// The accepted language is identical either way.
const VALID_URL_REGEX = new RegExp(
  '^(https?:\\/\\/)' + // protocol
    '((?:[a-zA-Z\\d](?:[a-zA-Z\\d-]*[a-zA-Z\\d])?\\.)+[a-zA-Z]{2,}|' + // domain
    '(?:\\d{1,3}\\.){3}\\d{1,3})' + // OR IP (v4)
    '(\\:(?:[1-9]\\d{0,3}|[1-5]\\d{4}|6[0-4]\\d{3}|65[0-4]\\d{2}|655[0-2]\\d|6553[0-5]))?' + // port
    '(\\/[-a-zA-Z\\d%_.~+@() ]*)*' + // path
    '(\\?[^\\s#]*)?' + // query
    '(\\#\\S*)?$',
  'i'
);

export function isValidUrl(input: string): boolean {
  return VALID_URL_REGEX.test(input);
}

// Complement of the path character class in VALID_URL_REGEX, plus `/` so separators
// survive. `%` is deliberately treated as safe: keys that already carry percent-escapes
// must not be double-encoded, and a literal `%` was always accepted. The `u` flag makes
// each match a whole code point, so astral characters (emoji) encode as one unit rather
// than as two lone surrogates.
const UNSAFE_ASSET_PATH_CHARS = /[^-a-zA-Z\d%_.~+@()/ ]/gu;

// encodeURIComponent is not usable here: it leaves `!`, `'` and `*` unescaped, and those are
// outside the accepted path class. Encode the UTF-8 bytes directly instead.
function percentEncode(char: string): string {
  return Array.from(new TextEncoder().encode(char))
    .map(byte => `%${byte.toString(16).toUpperCase().padStart(2, '0')}`)
    .join('');
}

// S3 keys are built from user-supplied file names, so they can contain characters that are
// legal in a URL but outside the subset isValidUrl accepts. Encode those before building an
// asset URL, otherwise a successful upload produces a URL the app then rejects on save.
export function encodeAssetPath(path: string): string {
  return path.replace(UNSAFE_ASSET_PATH_CHARS, percentEncode);
}

export function objectToQueryParams(obj) {
  let queryString = '';
  Object.keys(obj ?? {}).forEach(key => {
    if (queryString !== '') {
      queryString += `&${key}=${obj[key]}`;
    } else {
      queryString += `${key}=${obj[key]}`;
    }
  });
  return queryString;
}
